import { useEffect, useMemo, useState } from 'react';
import { SocketNamespace } from '@trinity/chat';
import type { Socket } from 'socket.io-client';
import { createLogger } from '../utils';
import { useGetSocket } from './useGetSocket';
import { useSetupDefaultSocketListeners } from './useSetupDefaultListeners';

export const useChatQuerySocket = (queryIdentifier: string, enabled = true): Socket | undefined => {
  const [socket, setSocket] = useState<Socket>();
  const getSocket = useGetSocket(SocketNamespace.Queries);

  const logger = useMemo(() => createLogger({
    namespace: SocketNamespace.Queries,
  }), []);

  useEffect(() => {
    if (!enabled) {
      logger.log('Query socket disabled, skipping setup');
      return;
    }

    if (!queryIdentifier) {
      logger.log('[WS Query] No queryIdentifier provided, skipping socket connection');
      return;
    }

    logger.log(`Setting up socket for query: ${queryIdentifier}`);

    const newSocket = getSocket();
    setSocket(newSocket);

    // Wait for connection before subscribing
    const handleConnect = () => {
      logger.log(`Socket connected, subscribing to: ${queryIdentifier}`);
      newSocket.emit('subscribe', queryIdentifier);
    };

    if (newSocket.connected) {
      handleConnect();
    } else {
      newSocket.on('connect', handleConnect);
    }

    logger.log(`Socket setup complete for: ${queryIdentifier}`);

    return () => {
      logger.log(`Cleaning up socket for query: ${queryIdentifier}`);
      newSocket.off('connect', handleConnect);
      newSocket.emit('unsubscribe', queryIdentifier);
      newSocket.disconnect();
      setSocket(undefined);
    };
  }, [enabled, getSocket, logger, queryIdentifier]);

  useSetupDefaultSocketListeners(socket, SocketNamespace.Queries);

  return socket;
};